import { merge } from 'lodash-es';
import { CHART_TYPES } from '../enums';
import { translateColor } from './dataset-color-config-builder';

const formatNumber = value => {
  return typeof value === 'string'
    ? value
    : Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
};

const colors = {
  'Approve': '#00BA1F',
  'Auto Approve': '#42c3ed',
  'Approve 3DS': '#5b59ea',
  '3DS': '#00D894',
  'Transactions': '#00D894',
  'All Transactions': '#00D894',
  'Frictionless': '#20E8D3',
  'Abandoned': '#F6BA27',
  'No Auth': '#F42BB0',
  'Decline': '#FF5959',
  'Risk': '#a61607',
  'Policy': '#f6ba28',
  'Hard Decline': '#f78a0e',
  'Authenticated': '#00A5FF',
  'Failed': '#B5BECC',
  'Authorized': '#4E95FF',
  'No Data': '#FF8A0D',
  'default': '#277f9a',
  'Not Reviewed': '#00a5ff',
  'other': '#B5BECC',
};

const isOthersNode = key => key?.includes('_other');

const getColor = (key, context) => {
  const flowConfig = context.config.series[0].aggregation.flow.config;
  const flowNodeConfig = flowConfig.find(item => item.label === key);
  if (flowNodeConfig && flowNodeConfig.color) {
    return translateColor(flowNodeConfig.color, context);
  }
  return isOthersNode(key) ? colors['other'] : colors[key] || colors['default'];
};


const enforceNodeMinimums = (data, minVisualNodeVolume) => data.reduce((result, dataPoint) => {
  const { parent, label, value } = dataPoint;
  if (!parent) return result;
  return [...result, {
    from: parent,
    to: label,
    flow: Math.max(value, minVisualNodeVolume),
    yFormatted: formatNumber(value),
    rawValue: value
  }];
}, []);


const combineChildVolume = data => data.reduce((result, { parent, value }) => {
  if (!parent) return result;
  return { ...result, [parent]: (result[parent] || 0) + value };
}, {});

const calculateOthers = (parentVolums, rawLabels) => {
  return Object.entries(parentVolums).reduce((result, [key, val]) => {
    const normalizedKey = key.trim();
    if (rawLabels[normalizedKey] !== val) {
      return { ...result, [`${normalizedKey}_others`]: rawLabels[normalizedKey] - val };
    }
    return result;
  }, {});
};

const buildOthers = (data, rawLabels, minVisualNodeVolume) => {
  const parentVolums = combineChildVolume(data);
  const others = calculateOthers(parentVolums, rawLabels);

  return Object.entries(others).reduce((result, [key, val]) => {
    const parent = key.split('_').at(0);
    return [...result, {
      from: parent,
      to: key,
      flow: Math.max(val, minVisualNodeVolume),
      yFormatted: formatNumber(val)
    }];
  }, []);
};


const calculateChildParentConnections = data => data.reduce((result, dataPoint) => {
  const calculatedVolume = (result[dataPoint.parent] || 0) + dataPoint.value;
  return { ...result, [dataPoint.parent]: calculatedVolume };
}, {});


const exposeUnderThresholdNodes = (data, threshold) => data.reduce((result, { from, flow }) => {
  const isBelowThreshold = flow <= threshold;
  return isBelowThreshold
    ? { ...result, [from]: (result[from] + 1) || 1 }
    : result;
}, {});


export default (config, chartJSConfig, context) => {
  const { series } = config;

  const showPercentages = config.dashboardOptions?.charts?.showSankeyPercentageChanges;

  const isSankey = series.every(({ seriesType }) => {
    return seriesType === CHART_TYPES.SANKEY.toUpperCase();
  });

  if (!isSankey) {
    return chartJSConfig;
  }

  const sankeyFlow = series.reduce((accumulator, { aggregation: { data, mode } }) => {
    const hideZeroValues = !!config.series[0].hideZeroValues;
    if (hideZeroValues) {
      data = data.filter(dataChunk => dataChunk.value > 0);
    }
    const minVisualNodeVolume = Math.max(...data.map(({ value }) => value)) * 0.075;

    // Create a label-value object
    const rawLabels = data.reduce((result, dataPoint) => {
      if (dataPoint.parent) {
        return result;
      }
      return { ...result, [dataPoint.label]: dataPoint.value };
    }, calculateChildParentConnections(data));

    const others = buildOthers(data, rawLabels, minVisualNodeVolume);

    const createFlow = () => {
      const dataPoints = enforceNodeMinimums(data, minVisualNodeVolume);
      const allDataPoints = [...dataPoints, ...others];
      const underThresholdNodes = exposeUnderThresholdNodes(allDataPoints, minVisualNodeVolume);

      return allDataPoints.map(dataPoint => {
        const { to, flow } = dataPoint;
        if (underThresholdNodes[to]) {
          const nodeVolume = flow === minVisualNodeVolume ? 0 : flow;
          dataPoint.flow = minVisualNodeVolume * underThresholdNodes[to] + nodeVolume;
        }
        return dataPoint;
      });
    };

    const createdFlow = createFlow();

    const createLabels = () => {
      const combinedNodeValues = createdFlow.reduce((result, dataPoint) => {
        const { to, rawValue } = dataPoint;
        result[to] = (result[to] || 0) + rawValue;
        return result;
      }, {});

      const nodes = { ...rawLabels, ...combinedNodeValues };

      return Object.entries(nodes).reduce((result, [key, val]) => {
        const formattedKey = isOthersNode(key) ? 'other' : key;
        const prefix = mode === 'SUM' ? '$' : '';
        return {
          ...result,
          [key]: `${formattedKey}>>>${prefix}${formatNumber(val)}`
        };
      }, {});
    };

    return {
      flowData: [...accumulator.flowData, ...createdFlow],
      flowLabels: { ...accumulator.flowLabels, ...createLabels() }
    };
  }, { flowData: [], flowLabels: {} });

  const configObject = {
    type: CHART_TYPES.SANKEY,
    data: {
      datasets: [{
        data: sankeyFlow.flowData,
        labels: sankeyFlow.flowLabels,
        colorFrom: c => getColor(c.dataset.data[c.dataIndex].from, context),
        colorTo: c => getColor(c.dataset.data[c.dataIndex].to, context),
        colorMode: 'from',
        size: 'max',
        showPercentages,
      }]
    }
  };

  const merged = merge(chartJSConfig, configObject);

  // Remove data points that don't include the flow properties
  merged.data.datasets = merged.data.datasets.map(dataset => {
    const filteredDataset = dataset.data.filter(point => point.to);
    return { ...dataset, data: filteredDataset };
  });

  return merged;
};
